import React, { useState } from 'react';
import { formatRelativeTime } from '../DeacCommons';
import Toast from 'react-native-toast-message';
import { translate } from '../../../services/translate';
import moment from 'moment';
import CustomButton from '../../../components/CustomButton';
import ExcelJS from 'exceljs';

interface Props {
    dashboardData: DeacDashboardData;
    beginIn?: number;
    endIn?: number;
    isCustomPeriod: boolean;
    periodMessage: string;
}

export default function DashboardReport({ dashboardData, beginIn, endIn, isCustomPeriod, periodMessage }: Props) {
    const [isGeneratingReport, setIsGeneratingReport] = useState<boolean>(false);

    async function getDashboardReport() {
        try {
            setIsGeneratingReport(true);
            const workbook = new ExcelJS.Workbook();
            const sheet = workbook.addWorksheet('Dashboard DEAC');

            const chartData1 = [
                { title: translate('scheduled'), value: dashboardData.vacanciesGeneralSummary.scheduled },
                { title: translate('opened'), value: dashboardData.vacanciesGeneralSummary.opened },
                { title: translate('inProgress'), value: dashboardData.vacanciesGeneralSummary.progress },
                { title: translate('finished'), value: dashboardData.vacanciesGeneralSummary.finished },
                { title: translate('canceled'), value: dashboardData.vacanciesGeneralSummary.canceled }
            ];

            const chartData2 = dashboardData.mostCommonApplicationCancellationReason.map(item => {
                return {
                    title: item.name,
                    value: item.quantity
                };
            });

            const chartData3 = dashboardData.workplaceWithMostVacancies.map(item => {
                return {
                    title: item.name,
                    value: item.quantity
                };
            });

            const chartData4 = dashboardData.workplacesWithMostDropout.map(item => {
                return {
                    title: item.name,
                    value: item.quantity
                };
            });

            const tableData = dashboardData?.agentsRanks;
            const table2Data = dashboardData?.recentVacancies;

            sheet.getCell('A1').value = translate('period');
            if (isCustomPeriod) {
                sheet.getCell('B1').value = periodMessage;
                sheet.getCell('C1').value = translate('from');
            } else {
                sheet.getCell('B1').value = moment(beginIn).format('DD/MM/YYYY');
                sheet.getCell('C1').value = translate('to');
            }
            sheet.getCell('D1').value = moment(endIn).format('DD/MM/YYYY');

            sheet.getCell('A3').value = translate('vacanciesGeneralSummary');
            for (let i = 0; i < chartData1.length; i++) {
                sheet.getCell(`A${4 + i}`).value = chartData1[i].title;
                sheet.getCell(`B${4 + i}`).value = chartData1[i].value;
            }

            sheet.getCell('D3').value = translate('mostCommonApplicationsCancellationReasons');
            for (let i = 0; i < chartData2.length; i++) {
                sheet.getCell(`D${4 + i}`).value = chartData2[i].title;
                sheet.getCell(`E${4 + i}`).value = chartData2[i].value;
            }

            sheet.getCell('A11').value = translate('workplacesWithMoreWithdrawals');
            for (let i = 0; i < chartData3.length; i++) {
                sheet.getCell(`A${12 + i}`).value = chartData3[i].title;
                sheet.getCell(`B${12 + i}`).value = chartData3[i].value;
            }

            sheet.getCell('D11').value = translate('workplacesWithMoreWithdrawals');
            for (let i = 0; i < chartData4.length; i++) {
                sheet.getCell(`D${12 + i}`).value = chartData4[i].title;
                sheet.getCell(`E${12 + i}`).value = chartData4[i].value;
            }

            sheet.getCell('A19').value = translate('agentsRanking');
            sheet.getCell('A20').value = '#';
            sheet.getCell('B20').value = translate('agentName');
            sheet.getCell('C20').value = translate('workedHours');
            sheet.getCell('D20').value = translate('workedWorkplaces');
            sheet.getCell('E20').value = translate('withdrawals');

            for (let i = 0; i < tableData.length; i++) {
                const row = 21 + i;
                sheet.getCell(`A${row}`).value = i + 1;
                sheet.getCell(`B${row}`).value = tableData[i].name;
                sheet.getCell(`C${row}`).value = tableData[i].totalWorkHours;
                sheet.getCell(`D${row}`).value = tableData[i].workplaceCount;
                sheet.getCell(`E${row}`).value = tableData[i].dropoutCount;
            }

            sheet.getCell('G3').value = translate('recentVacancies');
            sheet.getCell('G4').value = '#';
            sheet.getCell('H4').value = translate('activityTitle');
            sheet.getCell('I4').value = translate('workplace');
            sheet.getCell('J4').value = translate('vacancyLevels');
            sheet.getCell('K4').value = translate('since');

            for (let i = 0; i < table2Data.length; i++) {
                const row = 5 + i;
                sheet.getCell(`G${row}`).value = i + 1;
                sheet.getCell(`H${row}`).value = table2Data[i].title;
                sheet.getCell(`I${row}`).value = table2Data[i].workplace.name;
                sheet.getCell(`J${row}`).value = table2Data[i].levels.map(l => l.name).join(', ');
                sheet.getCell(`K${row}`).value = formatRelativeTime(table2Data[i].applicationsStart);
            }

            const buffer = await workbook.xlsx.writeBuffer();
            const blob = new Blob([buffer], {
                type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
            });
            const objectUrl = URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = objectUrl;
            link.download = 'dashboard.xlsx';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(objectUrl);
        } catch (err) {
            console.error(err);
            Toast.show({
                type: 'sentinelxError',
                text1: translate('unexpectedError'),
            });
        } finally {
            setIsGeneratingReport(false);
        }
    }

    return <CustomButton
        text={translate('exportCSV')}
        onPress={getDashboardReport}
        isLoading={isGeneratingReport}
    />;
}
